import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { EncyclopediaItem } from '@/models/encyclopedia-item.model';
import { getEncyclopediaItem } from '@/services/encyclopedia-item.service';
import EncyclopediaItemForm from './encyclopedia-item-form';
import LinkList from '@/pages/links/link-list';
import EncyclopediaRelationList from '../../relation/encyclopedia-relation-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="encyclopedia-item-form">Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="encyclopedia-item-links">Links</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="encyclopedia-story-chapters">Story Chapters</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;
  const storyChapterId = useLocation()?.state?.storyChapterId;

  const [activeTab, setActiveTab] = useState('encyclopedia-item-form');
  const [formObject, setFormObject] = useState<EncyclopediaItem|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<EncyclopediaItem> = await getEncyclopediaItem(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Encyclopedia Item: ${errorModel?.errorMessage}`);
        nav('encyclopedia-items');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Encyclopedia Item: ${formObject.name}` : 'New Encyclopedia Item'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='encyclopedia-items'> Encyclopedia Items</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'encyclopedia-item-form' && <EncyclopediaItemForm formObject={formObject} pageState={pageState} storyChapterId={storyChapterId}/>}
          {activeTab == 'encyclopedia-item-links' && id && <LinkList encyclopediaItemId={id}/>}
          {activeTab == 'encyclopedia-story-chapters' && id && <EncyclopediaRelationList encyclopediaItemId={id}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
